import { IconChevronDown } from '@tabler/icons-react'
import { ScrollIndicatorConfig } from '@/models/drive.ts'

interface ScrollDownIndicatorProps {
  targetId?: string
  className?: string
  /** Legacy: direct icon class override. Prefer `indicatorConfig` instead. */
  iconClassName?: string
  /** Config-based appearance per device. Takes precedence over iconClassName. */
  indicatorConfig?: ScrollIndicatorConfig
}

const APPEARANCE_CLASSES = {
  light: 'text-[white] drop-shadow-lg',
  dark: 'text-content opacity-80',
} as const

function getVisibilityClass(config: ScrollIndicatorConfig): string {
  if (config.desktop === 'hidden' && config.mobile === 'hidden') return 'hidden'
  if (config.desktop === 'hidden') return 'hidden md:hidden'
  if (config.mobile === 'hidden') return 'hidden md:flex'
  return ''
}

function getIconClass(config: ScrollIndicatorConfig): string {
  const desktopClass = config.desktop !== 'hidden' ? APPEARANCE_CLASSES[config.desktop] : ''
  const mobileClass = config.mobile !== 'hidden' ? APPEARANCE_CLASSES[config.mobile] : ''

  // Both same appearance
  if (mobileClass === desktopClass) return mobileClass

  // Different appearance per breakpoint
  if (config.mobile === 'hidden') return `md:${desktopClass.replace(/ /g, ' md:')}`
  if (config.desktop === 'hidden') return mobileClass

  // Mobile light, desktop dark (or vice versa): mobile-first + md override
  if (config.mobile === 'light' && config.desktop === 'dark') {
    return 'text-[white] drop-shadow-lg md:text-content md:drop-shadow-none md:opacity-80'
  }
  if (config.mobile === 'dark' && config.desktop === 'light') {
    return 'text-content opacity-80 md:text-[white] md:drop-shadow-lg md:opacity-100'
  }

  return desktopClass
}

export default function ScrollDownIndicator({ targetId, className = '', iconClassName, indicatorConfig }: ScrollDownIndicatorProps) {
  // If config says both hidden, don't render
  if (indicatorConfig && indicatorConfig.desktop === 'hidden' && indicatorConfig.mobile === 'hidden') {
    return null
  }

  function handleClick() {
    if (targetId) {
      const element = document.getElementById(targetId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
        return
      }
    }

    // Fallback: scroll down by one viewport height
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    })
  }

  const visibilityClass = indicatorConfig ? getVisibilityClass(indicatorConfig) : ''
  const resolvedIconClass = indicatorConfig
    ? getIconClass(indicatorConfig)
    : (iconClassName ?? 'text-content opacity-80')

  return (
    <div
      className={`flex justify-center cursor-pointer ${visibilityClass} ${className}`}
      onClick={handleClick}
    >
      <div className='animate-bounce p-2'>
        <IconChevronDown size={36} stroke={2.5} className={resolvedIconClass} />
      </div>
    </div>
  )
}
